// Lists that open from a control (DESIGN.md «Контролы»): the menu with its items, checks and submenus, the popover, and the select —
// a control of the system's height that opens the system's menu, or a searchable list when the choice is long.

import * as DM from '@radix-ui/react-dropdown-menu';
import * as PO from '@radix-ui/react-popover';
import { useState, type ReactNode } from 'react';
import { Icon } from './icons.tsx';
import { Kbd, cx } from './base.tsx';
import { CheckMark, Chevron } from './controls.tsx';
import { PickList } from './PickList.tsx';
import s from './ui.module.css';

export const Menu = DM.Root;
export const MenuTrigger = DM.Trigger;
export function MenuContent({ children, align = 'start', side, className }: { children: ReactNode; align?: 'start' | 'end' | 'center'; side?: 'top' | 'bottom' | 'left' | 'right'; className?: string }) {
  return (
    <DM.Portal>
      <DM.Content className={cx(s.menu, className)} align={align} side={side} sideOffset={4} collisionPadding={8}>{children}</DM.Content>
    </DM.Portal>
  );
}
export function MenuItem({ icon, children, onSelect, danger, shortcut, disabled }: { icon?: string; children: ReactNode; onSelect?: (e: Event) => void; danger?: boolean; shortcut?: string[]; disabled?: boolean }) {
  return (
    <DM.Item className={cx(s.item, danger && s.itemDanger)} onSelect={onSelect} disabled={disabled}>
      {icon && <Icon name={icon} size={15} />}
      <span className={s.itemText}>{children}</span>
      {shortcut && <Kbd keys={shortcut} />}
    </DM.Item>
  );
}
/** An item that turns something on or off; the menu stays open, so several can be changed in a row. */
export function MenuCheck({ checked, onChange, children, disabled }: { checked: boolean; onChange: (v: boolean) => void; children: ReactNode; disabled?: boolean }) {
  return (
    <DM.CheckboxItem className={s.item} checked={checked} disabled={disabled} onCheckedChange={(v) => onChange(v === true)} onSelect={(e) => e.preventDefault()}>
      <CheckMark checked={checked} /><span className={s.itemText}>{children}</span>
    </DM.CheckboxItem>
  );
}
export const MenuSeparator = () => <DM.Separator className={s.sep} />;
export const MenuLabel = ({ children }: { children: ReactNode }) => <DM.Label className={s.label}>{children}</DM.Label>;

// Submenus: «Раскладка» and its fields in the menu of a view (ViewMenu).
export const MenuSub = DM.Sub;
export function MenuSubTrigger({ icon, children }: { icon?: string; children: ReactNode }) {
  return <DM.SubTrigger className={s.item}>{icon && <Icon name={icon} size={15} />}<span className={s.itemText}>{children}</span><Chevron dir="right" /></DM.SubTrigger>;
}
export function MenuSubContent({ children }: { children: ReactNode }) {
  return <DM.Portal><DM.SubContent className={s.menu} sideOffset={6} collisionPadding={8}>{children}</DM.SubContent></DM.Portal>;
}

export const Popover = PO.Root;
export const PopoverTrigger = PO.Trigger;
export const PopoverAnchor = PO.Anchor;
export function PopoverContent({ children, align = 'start', width, className, onOpenAutoFocus, side }: { children: ReactNode; align?: 'start' | 'end' | 'center'; width?: number; className?: string; onOpenAutoFocus?: (e: Event) => void; side?: 'top' | 'bottom' | 'left' | 'right' }) {
  return (
    <PO.Portal>
      <PO.Content className={cx(s.popover, className)} align={align} side={side} sideOffset={4} collisionPadding={8} style={width ? { width } : undefined} onOpenAutoFocus={onOpenAutoFocus}>
        {children}
      </PO.Content>
    </PO.Portal>
  );
}

/**
 * One choice from a list: a control of the system's height that opens the system's menu, instead of the browser's own `select`
 * that looks different in every browser and theme (audit 28.10, M5). `value: ''` is a valid «none» option.
 * `search` — the list opens with a search on top (a combobox): for a list that grows with the data (events, records, people).
 * It is the caller's choice, never the count of options: one control keeps one way of working whatever the data.
 */
export function Select({ value, onChange, options, label, placeholder = 'Выберите…', className, disabled, size = 'md', invalid, search }: {
  value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; label: string; placeholder?: string; className?: string;
  disabled?: boolean; size?: 'sm' | 'md' | 'lg'; invalid?: boolean; search?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value);
  const trigger = (
    <button type="button" className={cx(s.select, size !== 'md' && s[`select_${size}`], className)} aria-label={label} aria-invalid={invalid || undefined} disabled={disabled}>
      <span className={cx(s.selectText, !current && s.selectEmpty)}>{current?.label ?? placeholder}</span><Chevron />
    </button>
  );
  if (search) {
    return (
      <PO.Root open={open} onOpenChange={setOpen}>
        <PO.Trigger asChild>{trigger}</PO.Trigger>
        <PopoverContent width={280}>
          <PickList options={options.map((o) => ({ id: o.value, label: o.label }))} selected={[value]} placeholder={label}
            onPick={(v) => onChange(v)} onDone={() => setOpen(false)} />
        </PopoverContent>
      </PO.Root>
    );
  }
  return (
    <DM.Root>
      <DM.Trigger asChild>{trigger}</DM.Trigger>
      <DM.Portal>
        <DM.Content className={s.menu} align="start" sideOffset={4} collisionPadding={8}>
          <DM.RadioGroup value={value} onValueChange={onChange}>
            {options.map((o) => (
              <DM.RadioItem key={o.value} value={o.value} className={s.item}>
                {/* The tick has its slot in every row, so the labels stand in one column whichever is chosen. */}
                <span className={s.itemTickSlot}><DM.ItemIndicator className={s.itemTick}><Icon name="tick" size={14} /></DM.ItemIndicator></span>
                <span className={s.itemText}>{o.label}</span>
              </DM.RadioItem>
            ))}
          </DM.RadioGroup>
        </DM.Content>
      </DM.Portal>
    </DM.Root>
  );
}
